/**
 * useMobileState Hook - Manages mobile-specific UI state
 * Handles selected track, active tab, step pagination, and panel visibility
 */

import { useState, useCallback } from 'react';

/**
 * Mobile tab types for bottom navigation
 */
export type MobileTab = 'grid' | 'tracks' | 'sound' | 'song';

/**
 * Panel types that can be opened/closed in mobile UI
 */
export type PanelType = 'tracks' | 'sound' | 'volume' | 'bpm' | 'steps' | 'menu' | 'trackActions' | 'aiGenerate';

/**
 * Panel visibility states
 */
export type PanelStates = Record<PanelType, boolean>;

/**
 * Return type for useMobileState hook
 */
export interface UseMobileStateReturn {
  /** Currently selected track index (0-based) */
  selectedTrackIndex: number;
  /** Set the selected track index */
  setSelectedTrackIndex: (index: number) => void;
  
  /** Active bottom tab */
  activeTab: MobileTab;
  /** Set the active tab */
  setActiveTab: (tab: MobileTab) => void;
  
  /** Current step page for pagination (0-based) */
  currentStepPage: number;
  /** Set the current step page */
  setCurrentStepPage: (page: number) => void;
  
  /** Panel visibility states */
  panelStates: PanelStates;
  /** Open a specific panel */
  openPanel: (panel: PanelType) => void;
  /** Close a specific panel */
  closePanel: (panel: PanelType) => void;
}

/**
 * Initial panel states - all panels closed by default
 */
const INITIAL_PANEL_STATES: PanelStates = {
  tracks: false,
  sound: false,
  volume: false,
  bpm: false,
  steps: false,
  menu: false,
  trackActions: false,
  aiGenerate: false,
};

/**
 * Custom hook for managing mobile-specific UI state
 * 
 * Manages state for:
 * - Selected track in Focus Mode
 * - Active bottom tab navigation
 * - Current step page for pagination
 * - Panel open/closed states
 * 
 * Initializes with sensible defaults:
 * - Track 0 selected
 * - Grid tab active
 * - Page 0 displayed
 * - All panels closed
 * 
 * State Preservation (Requirements 13.1, 13.2, 13.5, 10.7):
 * - All state values persist across tab switches and panel operations
 * - State is independent of panel visibility
 * - State is preserved when switching between mobile and desktop layouts
 * - Grid state and playback position are managed separately by useGrid hook
 * 
 * @param totalTracks - Total number of tracks in the composition
 * @returns Mobile state management interface
 * 
 * @example
 * ```tsx
 * function MobileComposer() {
 *   const {
 *     selectedTrackIndex,
 *     setSelectedTrackIndex,
 *     activeTab,
 *     setActiveTab,
 *     openPanel,
 *     closePanel
 *   } = useMobileState(4);
 *   
 *   return (
 *     <div>
 *       <TrackHeader 
 *         trackIndex={selectedTrackIndex}
 *         onEditTap={() => openPanel('sound')}
 *       />
 *       <BottomTabs 
 *         activeTab={activeTab}
 *         onTabChange={setActiveTab}
 *       />
 *     </div>
 *   );
 * }
 * ```
 */
export function useMobileState(_totalTracks: number): UseMobileStateReturn {
  // Selected track state - defaults to track 0
  const [selectedTrackIndex, setSelectedTrackIndex] = useState(0);
  
  // Active tab state - defaults to 'grid'
  const [activeTab, setActiveTab] = useState<MobileTab>('grid');
  
  // Current step page state - defaults to page 0
  const [currentStepPage, setCurrentStepPageInternal] = useState(0);
  
  // Wrap setCurrentStepPage with logging
  const setCurrentStepPage = useCallback((page: number) => {
    console.log('[useMobileState] setCurrentStepPage called:', { 
      oldPage: currentStepPage, 
      newPage: page,
      stack: new Error().stack?.split('\n').slice(2, 5).join('\n')
    });
    setCurrentStepPageInternal(page);
  }, [currentStepPage]);
  
  // Panel visibility states - all closed by default
  const [panelStates, setPanelStates] = useState<PanelStates>(INITIAL_PANEL_STATES);
  
  /**
   * Open a specific panel
   * Sets the panel's visibility state to true
   */
  const openPanel = useCallback((panel: PanelType) => {
    setPanelStates(prev => ({
      ...prev,
      [panel]: true,
    }));
  }, []);
  
  /**
   * Close a specific panel
   * Sets the panel's visibility state to false
   */
  const closePanel = useCallback((panel: PanelType) => {
    setPanelStates(prev => ({
      ...prev,
      [panel]: false,
    }));
  }, []);
  
  return {
    selectedTrackIndex,
    setSelectedTrackIndex,
    activeTab,
    setActiveTab,
    currentStepPage,
    setCurrentStepPage,
    panelStates,
    openPanel,
    closePanel,
  };
}
